<template>
  <footer class="border-t border-gray-200 mt-auto bg-[#f8f9fa] py-8 sm:py-10 md:py-12">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        <!-- Company Info -->
        <div class="sm:col-span-2 lg:col-span-1">
          <div class="flex items-center space-x-2 mb-4">
            <div
              class="w-8 h-8 rounded-lg bg-gradient-to-br from-[#fe6d0d63] to-[#ffb47c] flex items-center justify-center shadow-lg"
            >
              <TorukLogo class="w-5 h-5 text-white" />
            </div>
            <div class="flex items-baseline">
              <span
                class="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c]"
              >
                toruk
              </span>
              <span class="text-lg font-semibold text-[#fe6d0d] ml-1"> MAGIC&trade; </span>
              <span class="text-lg font-semibold text-[#fe6d0d] ml-1"> Deep </span>
              <span class="text-lg font-semibold text-[#fe6d0d] ml-1"> Tech </span>
            </div>
          </div>
          <p class="text-[#0b105d]/80 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
            {{ $t('torukMagic.description') }}
          </p>
        </div>

        <!-- Navigation -->
        <div>
          <h4
            class="text-base sm:text-lg font-semibold text-[#0b105d] mb-3 sm:mb-4 flex items-center"
          >
            <div
              class="w-1 h-6 bg-gradient-to-b from-[#fe6d0d] to-[#ffb47c] rounded-full mr-2"
            ></div>
            {{ $t('footer.navigation') }}
          </h4>
          <ul class="space-y-2 sm:space-y-3">
            <li v-for="item in navItems" :key="item.path">
              <router-link
                :to="item.path"
                class="text-[#0b105d]/70 hover:text-[#fe6d0d] transition-colors duration-300 text-sm sm:text-base flex items-center group"
              >
                <ChevronRightIcon
                  class="w-3 h-3 sm:w-4 sm:h-4 mr-1 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300 text-[#fe6d0d]/60 group-hover:text-[#fe6d0d]"
                />
                <span>{{ $t(item.name) }}</span>
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div>
          <h4
            class="text-base sm:text-lg font-semibold text-[#0b105d] mb-3 sm:mb-4 flex items-center"
          >
            <div
              class="w-1 h-6 bg-gradient-to-b from-[#fe6d0d] to-[#ffb47c] rounded-full mr-2"
            ></div>
            {{ $t('footer.contactUs') }}
          </h4>
          <ul class="space-y-2 sm:space-y-3 text-[#0b105d]/70">
            <li class="flex items-start group">
              <MapPinIcon
                class="h-4 w-4 sm:h-5 sm:w-5 mr-2 mt-0.5 text-[#fe6d0d] flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
              />
              <span class="text-sm sm:text-base">{{ $t('footer.address') }}</span>
            </li>
            <li class="flex items-center group">
              <PhoneIcon
                class="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[#fe6d0d] flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
              />
              <a
                :href="'tel:' + $t('footer.phone')"
                class="hover:text-[#fe6d0d] transition-colors duration-300 text-sm sm:text-base"
              >
                {{ $t('footer.phone') }}
              </a>
            </li>
            <li class="flex items-center group">
              <MailIcon
                class="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[#fe6d0d] flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
              />
              <a
                :href="'mailto:' + $t('footer.email')"
                class="hover:text-[#fe6d0d] transition-colors duration-300 text-sm sm:text-base break-all"
              >
                info&#64;torukdeep.tech
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200/70">
        <div class="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
          <div class="text-xs sm:text-sm text-[#0b105d]/60 text-center sm:text-left">
            Made with
            <span class="text-red-500 mx-1">♥</span> in Türkiye
          </div>
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2 text-xs sm:text-sm text-[#0b105d]/60">
              <p class="text-xs sm:text-sm text-[#0b105d]/60">
                {{ $t('footer.copyright') }} {{ $t('footer.magicSaaS') }}.
                {{ $t('footer.allRightsReserved') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import TorukLogo from '@/components/icons/ApplicationLogo.vue'
import { ChevronRightIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const navItems = [
  { name: 'nav.home', path: '/' },
  { name: 'nav.about', path: '/about' },
  { name: 'nav.contact', path: '/contact' },
]
</script>

<style scoped></style>
